import { FC, useCallback } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { waContactsCacheSelectOneObjectByQuery } from '@common-reducers/DBServiceThunks';
import { SelectedContact, UnifiedContactSelector } from './index';
import { getContactSerializedId } from './unified-contact-row';
import type { TypedRecipient } from '@common/utils/phone-validation-utils';

const stripIdSuffix = (id: string) => id.replace(/@(c\.us|g\.us|newsletter|lid)$/, '');

const buildSelectedContact = (id: string, source: any): SelectedContact => {
    const fallback = stripIdSuffix(id);
    const name = source?.name || source?.pushname || source?.shortName || fallback;
    return {
        id,
        name,
        displayName: source?.pushname || source?.name || fallback,
        imgUrl: source?.imgUrl,
    };
};

export interface ContactPickerFieldProps {
    /** Currently-selected contact ids (controlled). */
    value: string[];
    /** Rich chip data — one entry per id, in the same order as `value`. */
    selectedContacts: SelectedContact[];
    /** Called when a contact is added, with as much rich data as we resolved. */
    onContactSelected?: (contact: SelectedContact) => void;
    /** Called when a contact is removed (chip X or list-row toggle off). */
    onContactRemoved?: (id: string) => void;
    collapsible?: boolean;
    height?: number | string;
    helperText?: string;
    searchPlaceholder?: string;
    showRefreshButton?: boolean;
    className?: string;
    /** Passed to the selector's search box (see UnifiedContactSelector). */
    searchBoxClassName?: string;
}

/**
 * Chip-style multi-select contact picker. Wraps {@link UnifiedContactSelector}
 * with the toggle / free-input glue shared by every chip-mode caller (trigger
 * settings, scheduled message editor, …) so the chip behavior is identical
 * across the app.
 */
export const ContactPickerField: FC<ContactPickerFieldProps> = ({
    value,
    selectedContacts,
    onContactSelected,
    onContactRemoved,
    collapsible,
    height = 320,
    helperText,
    searchPlaceholder,
    showRefreshButton,
    className,
    searchBoxClassName,
}) => {
    const waContactsCache = useAppSelector(state => state.DBWaContactsCacheReducer);

    const handleClick = useCallback((contact: any) => {
        const id = getContactSerializedId(contact);
        if (!id) return;
        if (value.includes(id)) {
            onContactRemoved?.(id);
            return;
        }
        onContactSelected?.(buildSelectedContact(id, contact));
    }, [value, onContactSelected, onContactRemoved]);

    const handleFreeInput = useCallback(({ chatId: id, label }: TypedRecipient) => {
        if (value.includes(id)) return;
        const entity = waContactsCacheSelectOneObjectByQuery(waContactsCache, {
            'id._serialized': id,
        });
        // Not a contact: the chip shows the number the user saw on the row.
        onContactSelected?.(buildSelectedContact(id, entity ?? { name: label, pushname: label }));
    }, [value, onContactSelected, waContactsCache]);

    return (
        <UnifiedContactSelector
            multiSelect
            displaySelectedAsChips
            allowFreeInput
            showTypeFilter
            collapsible={collapsible}
            showRefreshButton={showRefreshButton}
            height={height}
            selectedContactIds={value}
            selectedContacts={selectedContacts}
            onContactClick={handleClick}
            onContactRemove={onContactRemoved}
            onFreeInputAdd={handleFreeInput}
            helperText={helperText}
            searchPlaceholder={searchPlaceholder}
            className={className}
            searchBoxClassName={searchBoxClassName}
        />
    );
};

export default ContactPickerField;
